import axios from "axios";
import { customFetch } from "../lib/network/Axios";
import { handleError } from "../lib/ErrorHandler";
import {
  KioskConfiguration,
  CreateKioskConfigPayload,
  UpdateKioskConfigPayload,
  CurrencyConfig,
  SubmitKioskConfigReviewPayload,
  KioskConfigurationReviewRequest,
  KioskCmsReviewStatus,
} from "../types/kioskConfig.types";

function getCurrencyName(code: string): string {
  try {
    return new Intl.DisplayNames(["en"], { type: "currency" }).of(code) || code;
  } catch {
    return code;
  }
}

function getCurrencySymbol(code: string): string {
  try {
    const parts = new Intl.NumberFormat("en", {
      style: "currency",
      currency: code,
      currencyDisplay: "narrowSymbol",
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).formatToParts(1);

    return parts.find((part) => part.type === "currency")?.value || code;
  } catch {
    return code;
  }
}

export const getKioskConfiguration = async (kioskId: string): Promise<KioskConfiguration> => {
  try {
    const response = await customFetch.get(`/kiosks/${kioskId}/configuration`);
    if (response?.status === 200 && response.data) {
      return response.data as KioskConfiguration;
    }
    throw new Error("Failed to fetch kiosk configuration");
  } catch (error) {
    handleError(error);
    if (axios.isAxiosError(error) && error.response?.status === 404) {
      throw new Error("Kiosk configuration not found");
    }
    throw error;
  }
};

export const createKioskConfiguration = async (
  kioskId: string,
  payload: CreateKioskConfigPayload,
): Promise<KioskConfiguration> => {
  try {
    const response = await customFetch.post(`/kiosks/${kioskId}/configuration`, payload);
    if (response?.status === 201 && response.data) {
      return response.data as KioskConfiguration;
    }
    throw new Error("Failed to create kiosk configuration");
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const updateKioskConfiguration = async (
  kioskId: string,
  payload: UpdateKioskConfigPayload,
): Promise<KioskConfiguration> => {
  try {
    const response = await customFetch.put(`/kiosks/${kioskId}/configuration`, payload);
    if (response?.status === 200 && response.data) {
      return response.data as KioskConfiguration;
    }
    throw new Error("Failed to update kiosk configuration");
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export interface OwnerPreferencesPayload {
  reportLiveEnabled?: boolean;
  reportWeeklyEnabled?: boolean;
  reportMonthlyEnabled?: boolean;
  payoutMode?: "CASH" | "PROGRAM";
  payoutProgramKey?: string;
  payoutProgramName?: string;
}

/** Owners save reporting/payout preferences here; the CMS PUT is reserved for admins. */
export const updateKioskOwnerPreferences = async (
  kioskId: string,
  payload: OwnerPreferencesPayload,
): Promise<KioskConfiguration> => {
  try {
    const response = await customFetch.put(`/kiosks/${kioskId}/owner-preferences`, payload);
    if (response?.status === 200 && response.data) {
      return response.data as KioskConfiguration;
    }
    throw new Error("Failed to update owner preferences");
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const submitKioskConfigurationReviewRequest = async (
  kioskId: string,
  payload: SubmitKioskConfigReviewPayload,
): Promise<KioskConfigurationReviewRequest> => {
  try {
    const response = await customFetch.post(`/kiosks/${kioskId}/configuration/review-request`, payload);
    if ((response?.status === 200 || response?.status === 201) && response.data) {
      return response.data as KioskConfigurationReviewRequest;
    }
    throw new Error("Failed to submit kiosk configuration review request");
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const listKioskConfigurationReviewRequests = async (
  params?: { status?: KioskCmsReviewStatus; kioskId?: string },
): Promise<KioskConfigurationReviewRequest[]> => {
  try {
    const response = await customFetch.get("/kiosks/configuration/review-requests", {
      params,
    });
    if (response?.status === 200 && Array.isArray(response.data)) {
      return response.data as KioskConfigurationReviewRequest[];
    }
    return [];
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const reviewKioskConfigurationRequest = async (
  requestId: number,
  payload: {
    action: "APPROVE" | "REJECT";
    comment?: string;
    approvedFields?: string[];
    rejectedFields?: string[];
  },
): Promise<KioskConfigurationReviewRequest> => {
  try {
    const response = await customFetch.post(
      `/kiosks/configuration/review-requests/${requestId}/review`,
      payload,
    );
    if ((response?.status === 200 || response?.status === 201) && response.data) {
      return response.data as KioskConfigurationReviewRequest;
    }
    throw new Error("Failed to review kiosk configuration request");
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const deleteKioskConfiguration = async (kioskId: string): Promise<void> => {
  try {
    await customFetch.delete(`/kiosks/${kioskId}/configuration`);
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const uploadImage = async (file: File): Promise<string> => {
  try {
    const formData = new FormData();
    // Reuse existing API upload contract from campaigns controller.
    formData.append("image", file);

    const response = await customFetch.post("/kiosks/configuration/upload-image", formData, {
      headers: {
        "Content-Type": undefined,
      },
    });

    const url =
      response?.data?.url ||
      response?.data?.data?.url ||
      response?.data?.result?.url ||
      null;

    if (typeof url === "string" && url.trim().length > 0) {
      return url;
    }

    throw new Error("Image upload response did not contain a usable URL");
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export const getSupportedCurrencies = async (): Promise<CurrencyConfig[]> => {
  try {
    const response = await customFetch.get("/exchangeratesapi/get-currency-list");
    const items = Array.isArray(response?.data) ? response.data : [];

    return items
      .map((item) => {
        const code = String(item?.symbol || "").trim().toUpperCase();
        if (!code) {
          return null;
        }

        return {
          code,
          symbol: getCurrencySymbol(code),
          name: getCurrencyName(code),
        } satisfies CurrencyConfig;
      })
      .filter((item): item is CurrencyConfig => Boolean(item));
  } catch (error) {
    handleError(error);
    throw error;
  }
};

export type CopyKioskConfigResult = {
  kioskId: string;
  kioskName: string;
  success: boolean;
  error?: string;
};

export type CopyKioskConfigSection =
  | "attract"
  | "donate"
  | "thanks"
  | "tiers"
  | "branding"
  | "currency";

export const copyKioskConfigToTargets = async (
  sourceKioskId: string,
  targetKiosks: { id: string; name: string }[],
  sections: CopyKioskConfigSection[],
): Promise<CopyKioskConfigResult[]> => {
  const sourceConfig = await getKioskConfiguration(sourceKioskId);
  const selectedSections = new Set(sections);
  const payload: UpdateKioskConfigPayload = {};

  if (selectedSections.has("attract")) {
    Object.assign(payload, {
      attractHeadline: sourceConfig.attractHeadline,
      attractHeadlineColor: sourceConfig.attractHeadlineColor,
      attractDescription: sourceConfig.attractDescription,
      attractDescriptionColor: sourceConfig.attractDescriptionColor,
      attractBackgroundImageUrl: sourceConfig.attractBackgroundImageUrl,
    });
  }

  if (selectedSections.has("donate")) {
    Object.assign(payload, {
      campaignId: sourceConfig.campaignId,
      donateHeadline: sourceConfig.donateHeadline,
      donateHeadlineColor: sourceConfig.donateHeadlineColor,
      donateDescription: sourceConfig.donateDescription,
      donateDescriptionColor: sourceConfig.donateDescriptionColor,
      donateBackgroundImageUrl: sourceConfig.donateBackgroundImageUrl,
      donateDailyGoal: sourceConfig.donateDailyGoal,
      textSettings: sourceConfig.textSettings,
    });
  }

  if (selectedSections.has("thanks")) {
    Object.assign(payload, {
      thanksHeadline: sourceConfig.thanksHeadline,
      thanksHeadlineColor: sourceConfig.thanksHeadlineColor,
      thanksDescription: sourceConfig.thanksDescription,
      thanksDescriptionColor: sourceConfig.thanksDescriptionColor,
      thanksBackgroundImageUrl: sourceConfig.thanksBackgroundImageUrl,
    });
  }

  if (selectedSections.has("tiers")) {
    payload.donationAmounts = sourceConfig.donationAmounts;
  }

  if (selectedSections.has("branding")) {
    Object.assign(payload, {
      partnerLogoUrl: sourceConfig.partnerLogoUrl,
      partnerName: sourceConfig.partnerName,
      showPartnerBranding: sourceConfig.showPartnerBranding,
    });
  }

  if (selectedSections.has("currency")) {
    Object.assign(payload, {
      currencyCode: sourceConfig.currencyCode,
      currencySymbol: sourceConfig.currencySymbol,
      currencyName: sourceConfig.currencyName,
    });
  }

  const results = await Promise.allSettled(
    targetKiosks.map((target) => updateKioskConfiguration(target.id, payload)),
  );

  return results.map((result, i) => ({
    kioskId: targetKiosks[i].id,
    kioskName: targetKiosks[i].name,
    success: result.status === "fulfilled",
    error: result.status === "rejected" ? String((result as PromiseRejectedResult).reason?.message || "Unknown error") : undefined,
  }));
};

export const getUserEmailSuggestions = async (search = ""): Promise<string[]> => {
  try {
    const response = await customFetch.get("/user", {
      params: search.trim().length > 0 ? { search: search.trim() } : undefined,
    });

    const users = Array.isArray(response?.data) ? response.data : [];

    const uniqueEmails = Array.from(
      new Set(
        users
          .map((user: any) => (typeof user?.email === "string" ? user.email.trim().toLowerCase() : ""))
          .filter((email: string) => email.length > 0),
      ),
    );

    return uniqueEmails;
  } catch (error) {
    handleError(error);
    throw error;
  }
};
